import { useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import PlainTextCell from 'components/PlainTextCell';

import useSort from 'utils/useSort';
import { parseDate } from 'utils/dates';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import Loading from 'components/Loading';
import { RightAlignedFlex } from '../components/styles';
import { useManagedOemSegmentsQuery } from './hooks/useManagedSegmentsQuery';

type ManagedOemSegmentType =
  ManagedOemSegmentsQuery['managedOemSegments']['collection'][0];
type ColumnProperties = Pick<
  ManagedOemSegmentType,
  'id' | 'name' | 'createdAt' | 'key' | 'totals' | 'managers'
>;

const OemSegments = () => {
  const { isSuperUser } = useCurrentUserContext();
  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedOemSegmentsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'name',
      title: t`OEM Segment Name`,
      DataCell: ({ rowData: { name } }) => <span>{name}</span>,
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`OEM Segment ID`,
      DataCell: ({ rowData: { id } }) => <span>{id}</span>,
      sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'key',
      title: t`Segment Key`,
      DataCell: ({ rowData: { key } }) => <span>{key}</span>,
      sortBy: (direction) => onSort('SEGMENT_KEY', direction),
    },
    {
      name: 'teams',
      title: t`Teams`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{totals?.sentinelTeams}</span>
      ),
      sortBy: (direction) => onSort('TEAMS_COUNT', direction),
    },
    {
      name: 'fleets',
      title: t`Accounts`,
      DataCell: ({ rowData: { totals } }) => <span>{totals?.fleets}</span>,
      sortBy: (direction) => onSort('FLEETS_COUNT', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totals } }) => <span>{totals?.assets}</span>,
      sortBy: (direction) => onSort('ASSETS_COUNT', direction),
    },
    {
      name: 'createdBy',
      title: t`Created by`,
      DataCell: ({ rowData: { createdBy } }) => (
        <span>{createdBy?.username || createdBy?.email}</span>
      ),
      sortBy: (direction) => onSort('CREATED_BY', direction),
    },
    {
      name: 'createdAt',
      title: t`Created on`,
      DataCell: ({ rowData: { createdAt } }) => {
        const [topLine, bottomLine] = parseDate(createdAt);
        return <PlainTextCell {...{ topLine, bottomLine }} />;
      },
      sortBy: (direction) => onSort('CREATED_AT', direction),
    },
    {
      name: 'admins',
      title: t`OEM Segment Admins`,
      DataCell: ({ rowData: { managers } }) => (
        <Flex>
          {managers.map(({ id, name }) => (
            <Tooltip target={<Avatar key={id} name={name} />}>{name}</Tooltip>
          ))}
        </Flex>
      ),
    },
  ];

  if (loading) return <Loading />;

  const tableData = data?.managedOemSegments.collection ?? [];
  const pagination = data?.managedOemSegments.pagination;

  return (
    <>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <Button
            disabled
            text={t`Create OEM Segment`}
            icon={Plus}
            size="small"
          />
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Segments by Name</Trans>
          </P>
          <SearchInput name="searchBySegment" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <Table
        data={tableData}
        columns={columns}
        onSort={onSort}
        defaultSort={defaultSort}
        footer={() => (
          <PaginationFooter
            title={t`Oem Segments`}
            setCurrentPage={setPage}
            totalOnPage={tableData.length}
            page={pagination?.page}
            perPage={pagination?.perPage}
            totalEntries={pagination?.totalEntries}
            totalPages={pagination?.totalPages}
          />
        )}
      />
    </>
  );
};

export default OemSegments;
